import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { FileText } from 'lucide-react';
import { useMemoryStore } from '../../stores/memoryStore';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';
import { MdLink } from '../common/MdLink';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const mdComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-xl font-bold text-text-primary mt-6 mb-3">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold text-text-primary mt-5 mb-2">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-medium text-text-primary mt-4 mb-2">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium text-text-primary mt-3 mb-1">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-sm text-text-secondary mb-3 leading-relaxed">{children}</p>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-accent-purple-light hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="text-sm text-text-secondary list-disc pl-6 mb-3 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="text-sm text-text-secondary list-decimal pl-6 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return <code className="text-sm font-mono">{children}</code>;
    }
    return (
      <code className="bg-bg-tertiary text-accent-purple-light px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-bg-tertiary border border-border-subtle rounded-lg p-4 overflow-x-auto mb-4 text-sm">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-accent-purple pl-4 italic text-text-muted mb-3">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-sm border border-border-subtle">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-bg-tertiary">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-text-primary border border-border-subtle">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-text-secondary border border-border-subtle">{children}</td>
  ),
  hr: () => (
    <hr className="border-border-subtle my-6" />
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-text-primary">{children}</strong>
  ),
};

export default function MarkdownViewer() {
  const { fileContent, contentLoading, selectedPath } = useMemoryStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top when file changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedPath]);

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!selectedPath || !fileContent) {
    return (
      <EmptyState
        icon={<FileText />}
        title="Select a file to view its contents"
        description="Choose a file from the tree on the left to view its markdown content"
      />
    );
  }

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto">
      <div className="p-6 max-w-4xl">
        {/* File header */}
        <div className="mb-6 pb-4 border-b border-border-subtle">
          <MdLink path={`workspace/${fileContent.path}`} label={fileContent.path} className="text-xs font-mono mb-1" />
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <span>Last modified: {formatDate(fileContent.lastModified)}</span>
            <span>{formatSize(fileContent.size)}</span>
          </div>
        </div>

        {/* Markdown content */}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {fileContent.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
